import { SNCR_PRESCRIPTION_TYPES, SNCR_TEXTS } from './constants';
import {
  DAILY_QUOTA_REACHED_CODE,
  MONTHLY_QUOTA_REACHED_CODE,
  NO_QUOTAS_AVAILABLE_CODE,
  UNKNOWN_ERROR_CODE,
  WRONG_PRESCRIBER_CODE,
  ANVISA_CFO_ERROR_CODE,
  ANVISA_CPF_ERROR_CODE,
  WRONG_PROFESSIONAL_NUMBER_CODE,
  UNKNOWN_INTERNAL_SERVER_ERROR_CODE,
} from './sncrTypes';
import {
  AddQuotaAlert,
  SncrErrorCode,
  SncrNumberingRequestItemResult,
  SncrNumberingRequestResult,
  SncrPrescriptionTypeConfig,
  SncrPrescriptionTypeId,
  SncrStock,
} from './types';

export function getTypeConfig(id: SncrPrescriptionTypeId): SncrPrescriptionTypeConfig {
  return SNCR_PRESCRIPTION_TYPES.find((type) => type.id === id)!;
}

export function isCriticalStock(stock: SncrStock): boolean {
  if (!stock.enabled) return false;
  return stock.balance < getTypeConfig(stock.type).criticalBalance;
}

export function sortStocks(stocks: SncrStock[]): SncrStock[] {
  const catalogOrder = SNCR_PRESCRIPTION_TYPES.map((type) => type.id);

  return [...stocks].sort((a, b) => catalogOrder.indexOf(a.type) - catalogOrder.indexOf(b.type));
}

export function getDefaultQuantity(config: SncrPrescriptionTypeConfig): number {
  return config.batch.kind === 'fixed' ? config.batch.value : config.batch.default;
}

export function getInitialQuantities(): Record<SncrPrescriptionTypeId, number> {
  return SNCR_PRESCRIPTION_TYPES.reduce(
    (quantities, config) => ({ ...quantities, [config.id]: getDefaultQuantity(config) }),
    {} as Record<SncrPrescriptionTypeId, number>
  );
}

export function adjustQuantity(config: SncrPrescriptionTypeConfig, current: number, direction: 1 | -1): number {
  return clampQuantity(config, current + direction);
}

export function clampQuantity(config: SncrPrescriptionTypeConfig, value: number): number {
  if (config.batch.kind === 'fixed') return config.batch.value;

  const { min, max } = config.batch;
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, Math.round(value)));
}

export function formatBalance(balance: number): string {
  return String(balance).padStart(2, '0');
}

export function mapRequestErrorToCode(error: unknown, forbiddenMeansAuthExpired = false): SncrErrorCode {
  const status = (error as { response?: { status?: number } })?.response?.status;

  if (status === 422) return 'professional_data_missing';
  if (status === 403 && forbiddenMeansAuthExpired) return 'auth_expired';
  return 'service_unavailable';
}

export type SncrResultKind = 'success' | 'partial' | 'failure';

export function getResultKind(result: SncrNumberingRequestResult): SncrResultKind {
  const fulfilledItems = result.items.filter((item) => item.status === 'fulfilled').length;
  if (fulfilledItems === result.items.length) return 'success';
  if (fulfilledItems > 0) return 'partial';
  return 'failure';
}

/**
 * Ordena os itens do resultado para o modal de feedback mantendo os que deram
 * certo (`fulfilled`) primeiro e movendo os que falharam (`failed`) para o fim.
 * A ordem relativa dentro de cada grupo é preservada (sort estável).
 *
 * Só reordena em falha parcial (mistura de `fulfilled` e `failed`). Em
 * sucesso total ou falha total a lista original já está na ordem correta,
 * então é retornada como veio.
 */
export function sortResultItemsByStatus(
  items: SncrNumberingRequestItemResult[]
): SncrNumberingRequestItemResult[] {
  const hasMixedStatus = items.some((item) => item.status === 'failed') && items.some((item) => item.status === 'fulfilled');
  if (!hasMixedStatus) return items;

  return [...items].sort((a, b) => Number(a.status === 'failed') - Number(b.status === 'failed'));
}

function findQuotaAlert(alerts: AddQuotaAlert[] | undefined, code: string): AddQuotaAlert | undefined {
  return alerts?.find((alert) => alert.code === code);
}

function hasQuotaAlert(items: SncrNumberingRequestItemResult[], code: string): boolean {
  return items.some((item) => Boolean(findQuotaAlert(item.alerts, code)));
}

/**
 * Mapa de mensagens por código de alerta, usado por item quando o backend
 * retorna sucesso na request (200) mas com falhas pontuais reportadas em
 * `alerts[]` por tipo de numeração. `UNKNOWN_ERROR` e `ANVISA_UNAUTHORIZED`
 * compartilham a mesma mensagem genérica de erro.
 */
const ALERT_CODE_MESSAGES: Record<string, string> = {
  [ANVISA_CFO_ERROR_CODE]: SNCR_TEXTS.anvisaCfoError,
  [UNKNOWN_INTERNAL_SERVER_ERROR_CODE]: SNCR_TEXTS.unknownInternalServerError,
};

function interpolate(template: string, data?: Record<string, unknown>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) => {
    const value = data?.[key];
    return value === undefined || value === null ? match : String(value);
  });
}

/**
 * Título/subtítulo da falha total. O Figma diferencia limite diário (notificações)
 * de limite mensal (receitas brancas, máx. 3 requisições/mês) a partir dos alerts
 * retornados por item pelo backend.
 */
export function getFailureHeader(result: SncrNumberingRequestResult): { title: string; subtitle: string } {
  const hasMonthly = hasQuotaAlert(result.items, MONTHLY_QUOTA_REACHED_CODE);
  const hasDaily = hasQuotaAlert(result.items, DAILY_QUOTA_REACHED_CODE);

  if (hasMonthly && !hasDaily) {
    return { title: SNCR_TEXTS.failureTitleMonthly, subtitle: SNCR_TEXTS.failureSubtitleMonthly };
  }
  if (hasDaily) {
    return { title: SNCR_TEXTS.failureTitle, subtitle: SNCR_TEXTS.failureSubtitleDaily };
  }
  return { title: SNCR_TEXTS.failureTitle, subtitle: SNCR_TEXTS.failureSubtitleGeneric };
}

export type ItemAlertVariant = 'warning' | 'danger';

/**
 * Define a variante visual (cor/ícone) do alerta do item a partir do `type`
 * retornado pelo backend em `alerts[]`. Havendo qualquer alerta `ERROR`,
 * prevalece a variante de erro; caso contrário, `WARNING` vira `warning`.
 * Sem alerts (falha genérica), mantém o fallback de erro.
 */
export function getItemAlertVariant(item: SncrNumberingRequestItemResult): ItemAlertVariant {
  if (!item.alerts?.length) return 'danger';
  return item.alerts.some((alert) => alert.type === 'ERROR') ? 'danger' : 'warning';
}

/**
 * Mensagem exibida no item que falhou. Usa o alert de limite (diário/mensal) com
 * os valores dinâmicos de limite e saldo enviados em `alert.data`. Sem alert
 * conhecido, faz fallback para a mensagem crua do backend ou o texto padrão.
 */
export function getItemAlertMessage(item: SncrNumberingRequestItemResult): string {
  const monthly = findQuotaAlert(item.alerts, MONTHLY_QUOTA_REACHED_CODE);
  if (monthly) return interpolate(SNCR_TEXTS.itemMonthlyLimitReached, monthly.data);

  const daily = findQuotaAlert(item.alerts, DAILY_QUOTA_REACHED_CODE);
  if (daily) {
    const hasBalance = Number(daily.data?.balance) > 0;
    const template = hasBalance ? SNCR_TEXTS.itemPartialDailyLimit : SNCR_TEXTS.itemDailyLimitReached;
    return interpolate(template, daily.data);
  }

  const noQuotaAvailable = findQuotaAlert(item.alerts, NO_QUOTAS_AVAILABLE_CODE);
  if (noQuotaAvailable) {
    const professionalInfo = noQuotaAvailable.data?.professionalInfo;
    if (!professionalInfo) return SNCR_TEXTS.itemNoQuotaAvailableGeneric;

    const prescriberInfo = `${professionalInfo.professionalRecordIssuer} ${professionalInfo.professionalRecord}-${professionalInfo.state}`;
    return interpolate(SNCR_TEXTS.itemNoQuotaAvailableWithPrescriber, { prescriberInfo });
  }

  const wrongProfessionalNumber = findQuotaAlert(item.alerts, WRONG_PROFESSIONAL_NUMBER_CODE);
  if (wrongProfessionalNumber) {
    const professionalInfo = wrongProfessionalNumber.data?.professionalInfo;
    if (!professionalInfo) return SNCR_TEXTS.itemWrongProfessionalNumberGeneric;

    const prescriberInfo = `${professionalInfo.professionalRecordIssuer} ${professionalInfo.professionalRecord}-${professionalInfo.state}`;
    return interpolate(SNCR_TEXTS.itemWrongProfessionalNumberWithPrescriber, { prescriberInfo });
  }

  const wrongPrescriber = findQuotaAlert(item.alerts, WRONG_PRESCRIBER_CODE);
  if (wrongPrescriber) {
    const professionalInfo = wrongPrescriber.data?.professionalInfo;
    const prescriberInfo = professionalInfo
      ? `${professionalInfo.professionalRecordIssuer} ${professionalInfo.professionalRecord}-${professionalInfo.state}`
      : 'Número do conselho';

    return interpolate(SNCR_TEXTS.itemWrongPrescriber, { prescriberInfo });
  }

  const anvisaCpfError = findQuotaAlert(item.alerts, ANVISA_CPF_ERROR_CODE);
  if (anvisaCpfError) {
    const professionalInfo = anvisaCpfError.data?.professionalInfo;
    const council = professionalInfo ? `${professionalInfo.professionalRecordIssuer}-${professionalInfo.state}` : 'conselho';

    return interpolate(SNCR_TEXTS.itemAnvisaCpfError, { council });
  }

  const unknownError = findQuotaAlert(item.alerts, UNKNOWN_ERROR_CODE);
  if (unknownError) {
    const status = unknownError.data?.anvisaResponseStatus;
    return interpolate(SNCR_TEXTS.itemUnknownError, { status: status ? `(${status})` : '' });
  }

  const knownAlert = item.alerts?.find((alert) => ALERT_CODE_MESSAGES[alert.code]);
  if (knownAlert) return ALERT_CODE_MESSAGES[knownAlert.code];

  return item.errorMessage || SNCR_TEXTS.quotaRequestFailed;
}
